import React, { useMemo } from "react";
import styles from "./index.module.scss";
import cn from "../../../Utils/class-names.utils";
import BaseInfoIcon from "../../icons/base-info.icon";
import SubscriptionExtendIcon from "../../icons/subscription-status/subscription-extend.icon";
import ClockIcon from "../../icons/subscription-status/clock.icon";

interface SubscriptionBillingProps {
  onExtendTrial: () => void;
  onConvertSubscription: () => void;
  isDistributorView?: boolean;
  isDistributorMspAccountInfo?: boolean;
}

const SubscriptionBilling = ({ onExtendTrial, onConvertSubscription, isDistributorView = false, isDistributorMspAccountInfo = false }: SubscriptionBillingProps) => {
  const trialData = {
    daysUsed: 13,
    totalDays: 15,
    daysRemaining: 11,
    maxDaysRemaining: 30,
    expirationDate: "11/11/2025",
  };

  const progressPercentage = (trialData.daysUsed / trialData.totalDays) * 100;

  const getProgressBarStyle = () => {
    const transitionValue = 70;
    const baseStyle = { width: `${progressPercentage}%` };
    
    if (progressPercentage >= 100) {
      return {
        ...baseStyle,
        background: `#F36060`,
      };
    }
    
    if (progressPercentage >= transitionValue) {
      const transitionPoint = (transitionValue / progressPercentage) * 100;
      
      return {
        ...baseStyle,
        background: `linear-gradient(90deg, #008FD4 ${transitionPoint}%, #F36060 100%)`,
      };
    }
    
    return baseStyle;
  };

  const progressBarStyle = useMemo(() => getProgressBarStyle(), [progressPercentage]);

  return (
    <section className={styles.subscription}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          Subscription and Billing
        </h2>
      </header>

      <div className={styles.content}>
        <div
          className={styles.trialBanner}
          role="alert"
          aria-live="polite"
        >
          <div className={styles.iconWrapper}>
            <BaseInfoIcon fill="currentColor" />
          </div>

          <div className={styles.bannerTextWrapper}>
            <p className={styles.bannerText}>
              15-day trial in progress — a great time to showcase value before
              talking upgrade.
            </p>
          </div>

          <div className={styles.closeButtonPlaceholder} />
        </div>

        <div className={styles.trialInfo}>
          <div className={styles.trialBadge}>
            <div className={styles.trialBadgeInner}>
              <span className={styles.trialBadgeText}>
                Trial
              </span>
            </div>
          </div>

          <div className={styles.trialDetails}>
            <p className={styles.trialDetailsText}>
              {trialData.daysRemaining} day(s) ({trialData.maxDaysRemaining}/
              {trialData.maxDaysRemaining} days max. remaining)
            </p>
          </div>

          <div className={styles.trialExpiry}>
            <BaseInfoIcon />
            <time
              className={styles.trialExpiryTime}
              dateTime="2025-11-11"
            >
              Expiring {trialData.expirationDate}
            </time>
          </div>
        </div>

        <div className={styles.trialProgress}>
          <div className={styles.trialProgressHeader}>
            <div className={styles.labelWrapper}>
              <label className={styles.label}>
                Trial Progress
              </label>
            </div>

            <div className={styles.usedWrapper}>
              <p className={styles.usedText}>
                {trialData.daysUsed} of {trialData.totalDays} days used
              </p>
            </div>
          </div>

          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={trialData.daysUsed}
            aria-valuemin={0}
            aria-valuemax={trialData.totalDays}
            aria-label="Trial progress"
          >
            <div
              className={styles.progressBarFill}
              style={progressBarStyle}
            />
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <button
          type="button"
          className={cn(styles.disableButton, "my-button")}
          aria-label="Disable Account"
        >
          <div className={styles.footerButtonText}>
            <span className={styles.disableText}>
              Disable Account
            </span>
          </div>
        </button>

        <div className={styles.footerActions}>
          {!isDistributorMspAccountInfo || !isDistributorView && (
            <button
              type="button"
              className={cn(
                styles.secondaryButton,
                "my-button styled",
                "d-flex align-items-center gap-2"
              )}
              aria-label="Extend Trial"
              onClick={onExtendTrial}
            >
              <div className={styles.secondaryButtonInner}>
                <ClockIcon fill="currentColor" width={20} height={20} />
              </div>

              <div className={cn(styles.footerButtonText, "text")}>
                <span className={styles.secondaryText}>Extend Trial</span>
              </div>
            </button>
          )}

          {!isDistributorView && (
            <button
            type="button"
            className={cn(
              styles.primaryButton,
              "my-button styled",
              "d-flex align-items-center gap-2"
            )}
            aria-label="Convert to Paid Plan"
            onClick={onConvertSubscription}
          >
            <div className={styles.primaryButtonInner}>
              <SubscriptionExtendIcon width={20} height={20} />
            </div>

            <div className={cn(styles.footerButtonText, "text")}>
              <span className={styles.primaryText}>Convert to Paid Plan</span>
            </div>
          </button>
          )}
        </div>
      </footer>


    </section>
  );
};

export default SubscriptionBilling;
